import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Archive, FolderInput, Mail as MailIcon } from "lucide-react";
import type { Folder, Message } from "@onemileauto/shared-types";
import { Button, DetailHeader } from "@onemileauto/ui";
import { useThread } from "../../hooks/use-thread";
import { useFolders } from "../../hooks/use-folders";
import { useSetThreadUnread } from "../../hooks/use-set-thread-unread";
import { moveThread as moveThreadRequest } from "../../lib/api-client";
import { mailboxQueryKeys } from "../../lib/query-keys";
import { displayParticipants } from "../../lib/format";
import { MessageCard, type MessageComposeMode } from "./message-card";
import { EmptyState } from "./empty-state";
import { ComposeSheet } from "./compose-sheet";
import { MoveToFolderSheet } from "./move-to-folder-sheet";

interface ComposeState {
  mode: MessageComposeMode;
  message: Message;
  replyAll?: boolean;
}

interface ThreadDetailProps {
  mailboxId: string | undefined;
  threadId: string;
  selfEmail?: string;
}

export function ThreadDetail({ mailboxId, threadId, selfEmail }: ThreadDetailProps) {
  const { data, isLoading, isError } = useThread(mailboxId, threadId);
  const { data: foldersData } = useFolders(mailboxId);
  const setThreadUnread = useSetThreadUnread(mailboxId);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [composeState, setComposeState] = useState<ComposeState | null>(null);
  const [movePickerOpen, setMovePickerOpen] = useState(false);

  const thread = data?.thread;
  const archiveFolderId = foldersData?.folders.find((f) => f.slug === "archive")?.id;

  // Opening a thread marks it read — see MailboxDO#setThreadUnread. Fires
  // once per unread thread (the optimistic update flips `unread` to false
  // in the cache immediately, so this effect doesn't re-fire on rerender).
  useEffect(() => {
    if (thread?.unread && mailboxId) {
      setThreadUnread.mutate({ threadId: thread.id, unread: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thread?.id, thread?.unread, mailboxId]);

  // Shared by the one-tap Archive button (fast path) and the "Move to
  // folder" picker (Phase 6, generalizes the same primitive to any folder,
  // system or custom) — both are just "move this thread to folderId",
  // consistent with the one-folder-per-thread model. Leaving the thread
  // detail view on success matches Gmail's own behavior: once a thread
  // moves out of the folder you're viewing it from, staying on it makes
  // less sense than returning to the list.
  const moveMutation = useMutation({
    mutationFn: (folderId: string) => moveThreadRequest(mailboxId!, threadId, folderId),
    onSuccess: () => {
      if (!mailboxId) return;
      queryClient.invalidateQueries({ queryKey: mailboxQueryKeys.threadsAll(mailboxId) });
      queryClient.invalidateQueries({ queryKey: mailboxQueryKeys.thread(mailboxId, threadId) });
      navigate({ to: "/", search: (prev) => prev });
    },
  });

  function handleMoveToFolder(folder: Folder) {
    setMovePickerOpen(false);
    moveMutation.mutate(folder.id);
  }

  if (isLoading) {
    return <div className="px-4 py-6 text-center text-sm text-muted">Loading&hellip;</div>;
  }

  if (isError || !thread) {
    return (
      <EmptyState
        icon={MailIcon}
        title="Couldn't open this conversation"
        description="It may have been moved. Go back and try again."
      />
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Single-pane layout: detail replaces the list in the same main
          pane at every viewport size, so this back control (not just
          browser back) needs to be visible everywhere, not just on
          mobile. */}
      <DetailHeader
        onBack={() => navigate({ to: "/", search: (prev) => prev })}
        backLabel="Back to list"
        title={thread.subject || "(no subject)"}
        subtitle={displayParticipants(thread.participants, selfEmail)}
        actions={
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setMovePickerOpen(true)}
              disabled={moveMutation.isPending}
              aria-label="Move to folder"
            >
              <FolderInput size={18} />
              <span className="hidden sm:inline">Move</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => archiveFolderId && moveMutation.mutate(archiveFolderId)}
              disabled={!archiveFolderId || moveMutation.isPending}
              aria-label="Archive conversation"
            >
              <Archive size={18} />
              <span className="hidden sm:inline">Archive</span>
            </Button>
          </div>
        }
      />

      {/* No pinned thread-level reply bar — Reply/Reply All/Forward are
          inline per-message actions (see message-card.tsx) so the user
          picks exactly which message in the thread they're acting on,
          rather than always composing against the last one. */}
      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-3 py-3">
        {thread.messages.map((message, i) => (
          <MessageCard
            key={message.id}
            message={message}
            mailboxId={mailboxId!}
            defaultExpanded={i === thread.messages.length - 1}
            onCompose={(mode, msg, replyAll) => setComposeState({ mode, message: msg, replyAll })}
          />
        ))}
      </div>

      {composeState && (
        <ComposeSheet
          mailboxId={mailboxId!}
          selfEmail={selfEmail}
          mode={composeState.mode}
          parentMessage={composeState.message}
          threadId={thread.id}
          replyAll={composeState.replyAll}
          onClose={() => setComposeState(null)}
        />
      )}

      {movePickerOpen && mailboxId && (
        <MoveToFolderSheet
          mailboxId={mailboxId}
          currentFolderId={thread.folderId}
          onMove={handleMoveToFolder}
          onClose={() => setMovePickerOpen(false)}
          moving={moveMutation.isPending}
        />
      )}
    </div>
  );
}
